import React, { useContext, useState, useEffect } from 'react'
import { AuthContext } from '../context/AuthContext'
import axios from 'axios'
import { showToast } from '../utils/toastUtils'
import {
  User,
  Mail,
  Calendar,
  Shield,
  MapPin,
  Phone,
  Camera,
  Edit3,
  LogOut,
  Briefcase,
  Lock,
  X,
  Eye,
  EyeOff
} from 'lucide-react'

function Profile() {
  const { user } = useContext(AuthContext)

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    department: "Other"
  })

  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: ""
  })

  const [isEditing, setIsEditing] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const BASE_URL = process.env.REACT_APP_API_URL

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || "",
        email: user.email || "",
        phone: user.phone || "",
        department: user.department || "Other"
      })
    }
  }, [user])

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value })
  }

  const handleSaveProfile = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem("token")
      const config = {
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
      }

      // Note: Backend route was set as PUT /api/auth/profile
      await axios.put(`${BASE_URL}/api/auth/profile`, formData, config)

      showToast("Profile Updated", "Your profile details have been successfully updated.", "success")
      setIsEditing(false)
    } catch (err) {
      console.error(err)
      showToast("Update Failed", err.response?.data?.message || "Failed to update profile", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleSavePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast("Password Mismatch", "New password and confirm password do not match.", "error")
      return
    }

    if (passwordData.newPassword.length < 6) {
      showToast("Weak Password", "Password must be at least 6 characters.", "error")
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem("token")
      const config = {
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
      }

      await axios.put(`${BASE_URL}/api/auth/profile`, { password: passwordData.newPassword }, config)

      showToast("Password Changed", "Your password has been updated securely.", "success")
      setShowPasswordModal(false)
      setPasswordData({ newPassword: "", confirmPassword: "" })
    } catch (err) {
      console.error(err)
      showToast("Error", err.response?.data?.message || "Failed to update password", "error")
    } finally {
      setLoading(false)
    }
  }

  // Modern input style
  const inputStyle = "w-full rounded-xl border-transparent bg-gray-50 text-gray-900 placeholder-gray-400 focus:border-transparent focus:bg-white focus:ring-0 focus:shadow-lg transition-all duration-200 ease-in-out py-3 px-4 font-medium disabled:opacity-60 disabled:cursor-not-allowed"

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* Banner */}
      <div className="h-48 bg-gradient-to-r from-blue-600 to-indigo-700 w-full relative">
        <div className="absolute inset-0 bg-black/10"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

          {/* Profile Header */}
          <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-end gap-6 border-b border-gray-100 pb-8">
            <div className="relative group">
              <div className="h-32 w-32 rounded-full ring-4 ring-white bg-white flex items-center justify-center shadow-md overflow-hidden relative">
                <div className="bg-gray-100 w-full h-full flex items-center justify-center">
                  <User className="h-16 w-16 text-gray-400" />
                </div>
              </div>
              <button className="absolute bottom-1 right-1 p-2.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 shadow-md transition-all transform hover:scale-110" title="Change Avatar">
                <Camera className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 text-center sm:text-left mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{formData.username || "User Name"}</h1>
              <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-100">
                  <Shield className="w-4 h-4 mr-1.5" />
                  {user?.role || "Team Member"}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200">
                  <MapPin className="w-4 h-4 mr-1.5" />
                  Sri Lanka
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (isEditing) {
                    handleSaveProfile();
                  } else {
                    setIsEditing(true);
                  }
                }}
                disabled={loading}
                className={`flex items-center gap-2 px-5 py-2.5 border rounded-xl text-sm font-semibold transition-all shadow-sm ${isEditing
                  ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                  : "border-gray-200 text-gray-700 bg-white hover:bg-gray-50"
                  }`}
              >
                <Edit3 className="w-4 h-4" /> {loading ? "Saving..." : (isEditing ? "Save Changes" : "Edit Profile")}
              </button>
            </div>
          </div>

          {/* Profile Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6 sm:p-8">

            {/* Left Column: Personal Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" /> Personal Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="relative group">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 ml-1">Full Name</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={inputStyle}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="relative group">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 ml-1">Email Address</label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`${inputStyle} pl-10`}
                        placeholder="name@example.com"
                      />
                      <Mail className="absolute left-3.5 top-3.5 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    </div>
                  </div>
                  <div className="relative group">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 ml-1">Phone Number</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`${inputStyle} pl-10`}
                        placeholder="+94 7X XXX XXXX"
                      />
                      <Phone className="absolute left-3.5 top-3.5 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    </div>
                  </div>
                  <div className="relative group">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 ml-1">Department</label>
                    <div className="relative">
                      <select
                        name="department"
                        value={formData.department}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`${inputStyle} pl-10 appearance-none`}
                      >
                        <option value="Other">Select...</option>
                        <option value="Finance">Finance</option>
                        <option value="Delivery">Delivery</option>
                        <option value="Leadership">Leadership</option>
                        <option value="Operations">Operations</option>
                        <option value="Sales">Sales</option>
                        <option value="Marketing">Marketing</option>
                      </select>
                      <Briefcase className="absolute left-3.5 top-3.5 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" /> Account Security
                </h3>
                <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-5 flex items-center justify-between transition-colors hover:bg-blue-50">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white text-blue-600 rounded-full shadow-sm">
                      <Shield className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Password</p>
                      <p className="text-sm text-gray-500">Last changed 3 months ago</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="px-4 py-2 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg text-sm hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column: Account Stats */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100/50 sticky top-6">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-6">Account Summary</h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm transition-shadow hover:shadow-md">
                    <span className="text-sm text-gray-500">Joined</span>
                    <span className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {user?.createdAt ? formatDate(user.createdAt) : "N/A"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm transition-shadow hover:shadow-md">
                    <span className="text-sm text-gray-500">Status</span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                      Active
                    </span>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200/50">
                  <button className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-red-100 text-red-600 bg-red-50/50 rounded-xl text-sm font-bold hover:bg-red-100 hover:border-red-200 transition-all">
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900">Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="••••••••"
                    className="w-full rounded-lg border-gray-300 pl-10 pr-10 focus:border-blue-500 focus:ring-blue-500"
                  />
                  <div
                    className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="••••••••"
                    className="w-full rounded-lg border-gray-300 pl-10 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 rounded-b-xl">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300 bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePassword}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
              >
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  )
}

export default Profile
